(() => {
  'use strict';

  if (window.MicasaGuidesI18n) {
    window.MicasaGuidesI18n.refresh();
    return;
  }

  const dataSelector = 'script[data-mc-guide-data]';
  const fieldSelector = '[data-mc-guide-field]';
  const fieldState = new WeakMap();
  const schemaState = new WeakMap();
  const bodyCache = new Map();
  let records = new Map();
  let queued = false;
  let suspended = false;
  let observer;
  let headState;

  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = value => typeof value === 'string' && value.trim() ? value : null;
  const language = () => {
    const selected = window.MicasaLanguage?.get?.();
    return selected === 'en' || selected === 'zh' ? selected : 'it';
  };
  const keyFor = value => {
    try {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin || !/\/blogs\/[^/]+\/[^/]+\/?$/.test(url.pathname)) return null;
      return url.origin + url.pathname.replace(/\/$/, '');
    } catch {
      return null;
    }
  };

  function readData() {
    const next = new Map();
    document.querySelectorAll(dataSelector).forEach(script => {
      let value;
      try { value = JSON.parse(script.textContent); } catch { return; }
      if (!object(value) || value.kind !== 'micasa-guide' || !object(value.it)) return;
      const key = keyFor(value.url);
      if (!key) return;
      const current = next.get(key);
      if (current?.detail && value.scope !== 'detail') return;
      const entry = { it: {}, en: {}, zh: {}, detail: value.scope === 'detail' };
      for (const locale of ['it', 'en', 'zh']) {
        if (!object(value[locale])) continue;
        for (const field of ['title', 'excerpt', 'seo_title', 'meta_description', 'cover_alt', 'body_html']) {
          const candidate = text(value[locale][field]);
          if (candidate !== null) entry[locale][field] = candidate;
        }
      }
      next.set(key, entry);
    });
    records = next;
  }

  function safeBody(value) {
    if (!text(value)) return null;
    if (bodyCache.has(value)) return bodyCache.get(value);
    const template = document.createElement('template');
    template.innerHTML = value;
    const allowed = new Set(['P', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI', 'A', 'STRONG', 'B', 'EM', 'I', 'SMALL', 'BR', 'SPAN', 'BLOCKQUOTE', 'DIV', 'FIGURE', 'FIGCAPTION', 'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'HR']);
    const blocked = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'SVG', 'MATH', 'TEMPLATE']);
    [...template.content.querySelectorAll('*')].forEach(element => {
      if (blocked.has(element.tagName)) {
        element.remove();
        return;
      }
      if (!allowed.has(element.tagName)) {
        element.replaceWith(...element.childNodes);
        return;
      }
      [...element.attributes].forEach(attribute => {
        const name = attribute.name.toLowerCase();
        if (!['href', 'title', 'colspan', 'rowspan'].includes(name) || (name === 'href' && element.tagName !== 'A')) {
          element.removeAttribute(attribute.name);
          return;
        }
        if (name === 'href') {
          try {
            const url = new URL(attribute.value, location.href);
            if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) element.removeAttribute('href');
          } catch { element.removeAttribute('href'); }
        }
      });
    });
    const result = template.content.textContent.trim() ? template.innerHTML : null;
    bodyCache.set(value, result);
    return result;
  }

  function localizedHref(raw) {
    try {
      const url = new URL(raw, location.href);
      if (!raw || raw.startsWith('#') || url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) return raw;
      return window.MicasaLanguage?.url?.(raw) || raw;
    } catch { return raw; }
  }

  function decorateLink(anchor) {
    if (!anchor || anchor.hasAttribute('download')) return;
    const raw = anchor.getAttribute('href');
    const next = localizedHref(raw);
    try {
      if (new URL(raw, location.href).href === new URL(next, location.href).href) return;
    } catch { return; }
    if (raw !== next) anchor.setAttribute('href', next);
  }

  function decorateBodyLinks(body) {
    body.querySelectorAll('a[href]').forEach(decorateLink);
  }

  function applyFields(locale) {
    document.querySelectorAll(fieldSelector).forEach(element => {
      const owner = element.closest('[data-mc-guide-url]');
      const entry = owner && records.get(keyFor(owner.getAttribute('data-mc-guide-url')));
      if (!entry) return;
      const field = element.getAttribute('data-mc-guide-field');
      if (!['title', 'excerpt', 'alt', 'body'].includes(field)) return;
      let state = fieldState.get(element);
      if (!state) {
        state = { native: field === 'body' ? element.innerHTML : field === 'alt' ? element.getAttribute('alt') : element.textContent, key: null };
        fieldState.set(element, state);
      }
      const translationField = field === 'alt' ? 'cover_alt' : field === 'body' ? 'body_html' : field;
      let translated = locale === 'it' ? null : text(entry[locale][translationField]);
      if (field === 'body' && translated !== null) translated = safeBody(translated);
      const next = translated === null ? state.native : translated;
      const stamp = locale + '\u0000' + next;
      if (field === 'body') {
        if (state.key !== stamp) {
          element.innerHTML = next;
          state.key = stamp;
        }
        const bodyLanguage = translated === null ? 'it' : locale === 'zh' ? 'zh-Hans' : locale;
        if (element.lang !== bodyLanguage) element.lang = bodyLanguage;
        decorateBodyLinks(element);
      } else if (field === 'alt') {
        if (element.getAttribute('alt') !== next) element.setAttribute('alt', next);
      } else if (element.textContent !== next) {
        element.textContent = next;
      }
      if (field === 'title') decorateLink(element.closest('a[href]'));
    });
    document.querySelectorAll('[data-mc-guide-original-language]').forEach(label => {
      const entry = records.get(keyFor(label.getAttribute('data-mc-guide-original-language')));
      const translatedBody = entry && (locale === 'it' ? safeBody(entry.en.body_html) || safeBody(entry.zh.body_html) : safeBody(entry[locale].body_html));
      label.hidden = Boolean(translatedBody);
    });
  }

  function captureHead() {
    if (headState) return;
    headState = { title: document.title, meta: new Map() };
    document.querySelector('title')?.setAttribute('data-micasa-no-translate', '');
    document.querySelectorAll('meta[name="description"],meta[property="og:title"],meta[property="og:description"],meta[property="og:image:alt"],meta[name="twitter:title"],meta[name="twitter:description"],meta[name="twitter:image:alt"]').forEach(element => {
      headState.meta.set(element, element.getAttribute('content'));
    });
  }

  function applyHead(locale) {
    const entry = records.get(keyFor(location.href));
    if (!entry?.detail) return;
    captureHead();
    const translation = locale === 'it' ? {} : entry[locale];
    const title = text(translation.seo_title) || headState.title;
    if (document.title !== title) document.title = title;
    headState.meta.forEach((native, element) => {
      const name = element.getAttribute('name') || element.getAttribute('property');
      const field = name.endsWith('image:alt') ? 'cover_alt' : name.endsWith('title') ? 'title' : 'meta_description';
      const next = text(translation[field]) || native;
      if (element.getAttribute('content') !== next) {
        if (next === null) element.removeAttribute('content');
        else element.setAttribute('content', next);
      }
    });
    document.querySelectorAll('script[data-mc-guide-schema]').forEach(script => {
      if (keyFor(script.getAttribute('data-mc-guide-schema')) !== keyFor(location.href)) return;
      let state = schemaState.get(script);
      if (!state) {
        try { state = { native: script.textContent, data: JSON.parse(script.textContent) }; } catch { return; }
        schemaState.set(script, state);
      }
      if (locale === 'it') {
        if (script.textContent !== state.native) script.textContent = state.native;
        return;
      }
      const data = JSON.parse(JSON.stringify(state.data));
      const body = safeBody(translation.body_html);
      const walk = node => {
        if (Array.isArray(node)) { node.forEach(walk); return; }
        if (!object(node)) return;
        const types = Array.isArray(node['@type']) ? node['@type'] : [node['@type']];
        if (types.some(type => ['Article', 'BlogPosting', 'NewsArticle'].includes(type))) {
          if (text(translation.title)) node.headline = translation.title;
          if (text(translation.meta_description)) node.description = translation.meta_description;
          if (body !== null) {
            const template = document.createElement('template');
            template.innerHTML = body;
            node.articleBody = template.content.textContent;
            node.inLanguage = locale === 'zh' ? 'zh-Hans' : locale;
          } else node.inLanguage = 'it';
        }
        Object.values(node).forEach(value => { if (value && typeof value === 'object') walk(value); });
      };
      walk(data);
      const serialized = JSON.stringify(data).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
      if (script.textContent !== serialized) script.textContent = serialized;
    });
  }

  function refresh() {
    queued = false;
    if (suspended) return;
    observer?.disconnect();
    try {
      readData();
      const locale = language();
      applyFields(locale);
      applyHead(locale);
    } finally {
      observer?.observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['href'] });
    }
  }

  function schedule() {
    if (queued || suspended) return;
    queued = true;
    queueMicrotask(refresh);
  }

  function relevant(node) {
    const element = node.nodeType === 1 ? node : node.parentElement;
    return element && (element.matches?.(dataSelector + ',' + fieldSelector) || element.closest?.(dataSelector + ',' + fieldSelector) || element.querySelector?.(dataSelector + ',' + fieldSelector));
  }

  function boot() {
    observer = new MutationObserver(changes => {
      if (changes.some(change => relevant(change.target) || [...change.addedNodes].some(relevant))) schedule();
    });
    refresh();
  }

  window.MicasaGuidesI18n = { refresh: schedule };
  window.addEventListener('micasa:languagechange', schedule);
  window.addEventListener('pagehide', () => {
    suspended = true;
    observer?.disconnect();
  });
  window.addEventListener('pageshow', () => {
    suspended = false;
    schedule();
  });
  window.addEventListener('popstate', schedule);
  window.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('[data-mc-guide-field="body"] a[href]');
    if (anchor) decorateBodyLinks(anchor.closest('[data-mc-guide-field="body"]'));
    const title = event.target.closest?.('[data-mc-guide-field="title"]');
    if (title) decorateLink(title.closest('a[href]'));
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
